import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Smartphone,
  Youtube
} from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Digital Marketing', path: '/services/digital-marketing' },
    { name: 'Web Development', path: '/services/web-development' },
    { name: 'App Development', path: '/services/app-development' },
  ];

  const company = [
    { name: 'About Us', path: '/about' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Clients', path: '/clients' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/colordroponline' },
    { name: 'Twitter', icon: Twitter, href: 'https://x.com/colordroponline' },
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/colordroponline/' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/company/colordroponline' },
    { name: 'Youtube', icon: Youtube, href: 'https://www.youtube.com/@colordroponline' },
  ];

  return (
    <footer className="bg-neutral-900 text-white">
      <div className="container-custom">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="lg:col-span-1"
          >
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <img src="/logo.png" alt="Color Drop" width={200} height={100} />
            </Link>
            {/* <p className="text-neutral-300 mb-6 leading-relaxed">
              We Grow<br/>
              Your Business
            </p> */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center hover:bg-primary-600 transition-colors duration-300"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </motion.div>


          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: false }}
          >
            <h3 className="text-xl font-serif font-semibold mb-6">India Office</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-neutral-300">
                    305, Veda Business Park,<br />
                    Bhawarkua, Indore, MP, India
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Smartphone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="tel:+919999206699"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  +91 99992 06699
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="tel:07314266699"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  +91 73142 66699
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="mailto:contact@colordroponline.com"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  contact@colordroponline.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: false }}
          >
            <h3 className="text-xl font-serif font-semibold mb-6">Europe Office</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-neutral-300">
                    Oslo, Norway, Europe
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Smartphone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="tel:+4796745494"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  +47 96745494
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="mailto:europe@colordroponline.com"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  europe@colordroponline.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: false }}
          >
            <h3 className="text-xl font-serif font-semibold mb-6">USA Office</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-neutral-300">
                    Houston, Texas, US
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Smartphone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="tel:+919999206699"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  +91 99992 06699
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="mailto:usa@colordroponline.com"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-300"
                >
                  usa@colordroponline.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: false }}
          className="py-6 border-t border-neutral-800 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <p className="text-neutral-400 text-sm">
            © {currentYear} Color Drop. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
